import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, User, ShoppingBag, Menu, X, Heart } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/legacy", label: "Legacy Edition" },
  { to: "/our-story", label: "Our Story" },
  { to: "/fit-guide", label: "Fit Guide" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 bg-poche-black/90 backdrop-blur-md border-b border-white/5">
      <div className="hidden md:flex justify-center py-2 text-[10px] tracking-luxury uppercase text-white/50 border-b border-white/5">
        <span data-testid="header-announce">Complimentary worldwide shipping &nbsp;·&nbsp; 2-year warranty &nbsp;·&nbsp; Built for real faces</span>
      </div>
      <div className="flex items-center justify-between px-6 md:px-10 py-5">
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>

        <Link to="/" data-testid="logo-home" className="flex items-baseline gap-3">
          <span className="font-serif text-2xl tracking-luxury text-poche-gold">POCHE</span>
          <span className="hidden lg:inline text-[10px] tracking-luxury uppercase text-white/40">Joseph Poche</span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => {
            const active = location.pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[11px] tracking-luxury uppercase transition-colors ${
                  active ? "text-poche-gold" : "text-white/80 hover:text-poche-gold"
                }`}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-5 text-white">
          <button data-testid="search-btn" className="hover:text-poche-gold transition-colors" aria-label="Search">
            <Search size={18} strokeWidth={1.5} />
          </button>
          <Link
            to={user ? "/account" : "/login"}
            data-testid="account-link"
            className="hover:text-poche-gold transition-colors"
            aria-label="Account"
          >
            <User size={18} strokeWidth={1.5} />
          </Link>
          <Link to="/cart" data-testid="cart-link" className="relative hover:text-poche-gold transition-colors" aria-label="Cart">
            <ShoppingBag size={18} strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-poche-gold text-poche-black text-[10px] w-4 h-4 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 bg-poche-black px-6 py-6 space-y-5">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className="block text-sm tracking-luxury uppercase text-white/80 hover:text-poche-gold"
            >
              {n.label}
            </Link>
          ))}
          <button
            data-testid="mobile-app-link"
            onClick={() => { setOpen(false); navigate("/app"); }}
            className="block text-sm tracking-luxury uppercase text-poche-gold"
          >
            Open Mobile App →
          </button>
        </div>
      )}
    </header>
  );
}
